import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import { Store } from "@ngxs/store";
import { OrganizationState } from "@vp/data-access/organization";
import { TagsState } from "@vp/data-access/tags";
import { Organization, Tag } from "@vp/models";
import { filterNullMap } from "@vp/shared/operators";

import { FormControl } from "@angular/forms";
import { compare } from "@vp/shared/utilities";
import { combineLatest, Observable, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { TagSelector, TagSelectorState } from "./state/tag-selector-state";
import * as TagSelectorActions from "./state/tag-selector-state.actions";
import { TagSelection, TagSelectorContext } from "./tag-selector-context.service";

@Component({
  selector: "vp-tag-selector",
  standalone: false,
  templateUrl: "./tag-selector.component.html",
  styleUrls: ["./tag-selector.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TagSelectorComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  selectors$: Observable<TagSelector[]> = inject(Store).select(TagSelectorState.getSelectors);
  organization$: Observable<Organization | null> = inject(Store).select(
    OrganizationState.organization
  );
  tags$: Observable<Tag[]> = inject(Store).select(TagsState.tags);

  @Input() tagTypeFriendlyId!: string;
  @Input() selectedTagId: string | undefined | null = null;
  @Input() noneSelectedText = "All";
  @Input() disableFixedInput = false;
  @Input() disabled = false;
  @Input() key = "";
  @Output() changedEvent = new EventEmitter<TagSelection>();

  formControl = new FormControl({ value: "", disabled: this.disabled });

  private readonly destroyed$$ = new Subject<void>();

  constructor(
    private readonly store: Store,
    private tagSelectorContext: TagSelectorContext
  ) {}

  activeSelector$ = this.selectors$.pipe(
    takeUntil(this.destroyed$$),
    map(selectors => {
      return selectors.find(
        s => s.tagType.friendlyId === this.tagTypeFriendlyId && s.key === this.key
      );
    }),
    filterNullMap()
  );

  availableTags$ = this.activeSelector$.pipe(
    map(selector => {
      if (selector?.tagType.tagTypeFriendlyPathId) {
        const pathParts = selector.tagType.tagTypeFriendlyPathId.split(".");
        const selectors = this.store.selectSnapshot(TagSelectorState.selectors);
        const parentSelector = selectors.find(
          s => s.tagType.friendlyId === pathParts[pathParts.length - 1] && s.key === this.key
        );
        if (parentSelector?.selectedTag) {
          return selector.tags
            .filter(tag => {
              const pathParts = tag.tagPath?.split(".");
              return (
                pathParts && pathParts[pathParts.length - 1] === parentSelector?.selectedTag?.tagId
              );
            })
            .filter(t => t.active === true)
            .sort((a, b) => compare(a.displayName, b.displayName, true));
        }
        return [];
      }
      /**
       * IF the tag type has no parent OR the tag selectors has no selector defined for
       * the parent then simply return the tags filtered by the tag type friendly id.
       */
      return selector.tags
        .filter(t => t.active === true)
        .sort((a, b) => compare(a.displayName, b.displayName, true));
    })
  );

  tagType$ = this.organization$.pipe(
    filterNullMap(),
    takeUntil(this.destroyed$$),
    map((org: Organization) => {
      const x = org.tagTypes.find(t => t.friendlyId === this.tagTypeFriendlyId);
      return x;
    }),
    filterNullMap()
  );

  ngOnInit(): void {
    this.tagSelectorContext.setSelectorKey(this.key);
    this.tagSelectorContext.addSelector(this.tagTypeFriendlyId);

    this.tagSelectorContext.selectors$
      .pipe(takeUntil(this.destroyed$$))
      .subscribe((selectors: TagSelector[]) => {
        const selector = selectors.find(s => s.tagType.friendlyId === this.tagTypeFriendlyId);
        if (selector) {
          this.formControl.setValue(selector.selectedTag?.tagId ?? null);
        }
      });

    combineLatest([
      this.availableTags$.pipe(map(tags => tags.length > 0)),
      this.activeSelector$.pipe(map(selector => selector?.fixed ?? false))
    ])
      .pipe(
        takeUntil(this.destroyed$$),
        map(([hasTags, isFixed]: [boolean, boolean]) => {
          return hasTags == false || isFixed == true;
        })
      )
      .subscribe((isDisabled: boolean) => {
        isDisabled ? this.formControl.disable() : this.formControl.enable();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.formControl.setValue(changes.selectedTagId?.currentValue ?? null);
  }

  ngAfterViewInit(): void {
    if (this.selectedTagId) {
      this.tagSelectorContext.selectTagFor(this.tagTypeFriendlyId, this.selectedTagId);
    }
  }

  ngOnDestroy(): void {
    this.store.dispatch(new TagSelectorActions.DeleteSelector(this.tagTypeFriendlyId, this.key));
    this.destroyed$$.next();
    this.destroyed$$.complete();
  }

  selectionChanged(tagId: string) {
    this.tagSelectorContext.selectTagFor(this.tagTypeFriendlyId, tagId);
    this.changedEvent.emit({
      tagTypeFriendlyId: this.tagTypeFriendlyId,
      tagId: tagId
    });
  }
}
